/**
 * imageVariants.js
 *
 * Given a Firebase Storage download URL (or any URL) that points at an
 * original listing image, derive URLs for the thumbnail/medium variants
 * produced by `functions/imageThumbnailsFn.js`.
 *
 * Variants live in the same Storage "folder" as the original, with the
 * filename prefixed by `thumb_320_` or `med_800_` and the extension swapped
 * to .webp / .jpg. Because the thumbnail function reuses the original's
 * download token (and persists one if missing), the same `?alt=media&token=`
 * query string can be reused for the variants.
 *
 * The resolver tolerates:
 *   - Firebase Storage v0 URLs:   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>?alt=media&token=...
 *   - Google Cloud Storage URLs:  https://storage.googleapis.com/<bucket>/<path>
 *   - Arbitrary external URLs (returned unchanged — no variants)
 *
 * If a URL doesn't look like a Firebase/GCS object we own, `getImageVariants`
 * returns just `{ full: url }` and consumers should render the original.
 */

const VARIANTS = {
  thumb: { prefix: "thumb_320_", width: 320 },
  med: { prefix: "med_800_", width: 800 },
};

const WATCHED_PREFIXES = [
  "businessListings/",
  "realEstateListings/",
  "roommateListings/",
  "events/",
];

function isWatchedPath(objectPath) {
  return WATCHED_PREFIXES.some((p) => objectPath.startsWith(p));
}

function stripExt(filename) {
  const i = filename.lastIndexOf(".");
  return i > 0 ? filename.slice(0, i) : filename;
}

function joinPosix(dir, name) {
  if (!dir || dir === ".") return name;
  return `${dir.replace(/\/+$/, "")}/${name}`;
}

/**
 * Parse a Firebase Storage download URL into { bucket, objectPath, query }.
 * Returns null if it doesn't match the expected shape.
 */
function parseFirebaseStorageUrl(url) {
  try {
    const u = new URL(url);
    // Firebase v0 download URL form
    if (
      u.hostname === "firebasestorage.googleapis.com" ||
      u.hostname.endsWith(".firebasestorage.googleapis.com")
    ) {
      // /v0/b/<bucket>/o/<encoded-path>
      const m = u.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/);
      if (!m) return null;
      const bucket = decodeURIComponent(m[1]);
      const objectPath = decodeURIComponent(m[2]);
      return {
        kind: "firebase",
        bucket,
        objectPath,
        search: u.search, // includes ?alt=media&token=...
        origin: `${u.protocol}//${u.host}`,
      };
    }
    // Plain GCS URL form
    if (u.hostname === "storage.googleapis.com") {
      const parts = u.pathname.replace(/^\//, "").split("/");
      if (parts.length < 2) return null;
      const bucket = parts.shift();
      const objectPath = parts.join("/");
      return {
        kind: "gcs",
        bucket,
        objectPath,
        search: u.search,
        origin: `${u.protocol}//${u.host}`,
      };
    }
  } catch {
    return null;
  }
  return null;
}

function buildVariantUrl(parsed, variantPrefix, ext) {
  const slash = parsed.objectPath.lastIndexOf("/");
  const dir = slash >= 0 ? parsed.objectPath.slice(0, slash) : "";
  const basename = slash >= 0 ? parsed.objectPath.slice(slash + 1) : parsed.objectPath;
  const variantBase = `${variantPrefix}${stripExt(basename)}.${ext}`;
  const variantPath = joinPosix(dir, variantBase);

  if (parsed.kind === "firebase") {
    return `${parsed.origin}/v0/b/${encodeURIComponent(parsed.bucket)}/o/${encodeURIComponent(variantPath)}${parsed.search || ""}`;
  }
  // gcs
  return `${parsed.origin}/${parsed.bucket}/${variantPath.split("/").map(encodeURIComponent).join("/")}${parsed.search || ""}`;
}

/**
 * Derive variant URLs from an original image URL.
 *
 * @param {string} url Original image URL.
 * @returns {{ full: string, med?: string, thumb?: string, srcSet?: string }}
 */
export function getImageVariants(url) {
  if (!url || typeof url !== "string") return { full: url };
  const parsed = parseFirebaseStorageUrl(url);
  if (!parsed) return { full: url };
  if (!isWatchedPath(parsed.objectPath)) return { full: url };

  // Skip variants of variants (defensive — shouldn't happen, but safe).
  const basename = parsed.objectPath.split("/").pop() || "";
  if (basename.startsWith(VARIANTS.thumb.prefix) || basename.startsWith(VARIANTS.med.prefix)) {
    return { full: url };
  }

  // Prefer .webp variants — broadly supported in 2026 and significantly
  // smaller than equivalent JPEGs.
  const thumb = buildVariantUrl(parsed, VARIANTS.thumb.prefix, "webp");
  const med = buildVariantUrl(parsed, VARIANTS.med.prefix, "webp");

  const srcSet = `${thumb} ${VARIANTS.thumb.width}w, ${med} ${VARIANTS.med.width}w, ${url} 1600w`;

  return { full: url, med, thumb, srcSet };
}

/** Convenience: return just the thumbnail URL or fall back to original. */
export function getThumbnailUrl(url) {
  return getImageVariants(url).thumb || url || "";
}

/** Convenience: return just the medium URL or fall back to original. */
export function getMediumUrl(url) {
  return getImageVariants(url).med || url || "";
}
